import { type PropsWithChildren, useRef } from "react";
import { Button } from "react-aria-components";
import { cn, tv } from "tailwind-variants";
import type { AlertProps } from "#/_packages/umbrella/features/alerts/types";

export const alert = tv({
	base: "alert",
	variants: {
		intent: {
			primary: "alert-intent-primary",
			secondary: "alert-intent-secondary",
			light: "alert-intent-light",
			dark: "alert-intent-dark",
			white: "alert-intent-white",
			black: "alert-intent-black",
			success: "alert-intent-success",
			warning: "alert-intent-warning",
			danger: "alert-intent-danger",
			info: "alert-intent-info",
			none: "alert-intent-none",
		},
	},
	defaultVariants: {
		intent: "primary",
	},
});

const alertLayout = tv({
	slots: {
		container: "container",
		content: "flex items-start gap-2",
		icon: "icon shrink-0",
		body: "alert-body [&>*:last-child]:mb-0",
		dismissButton: "btn btn-sm btn-ghost btn-icon-only shrink-0",
		dismissIcon: "icon",
		visuallyHidden:
			"sr-only absolute h-px w-px overflow-hidden whitespace-nowrap border-0 p-0",
	},
});

function DismissIcon() {
	return (
		<svg
			className={alertLayout().dismissIcon()}
			aria-hidden="true"
			viewBox="0 0 24 24"
		>
			<use href="/umbrella/ui/icons/lucide-x.svg#icon" />
		</svg>
	);
}

export function Alert({
	id,
	intent,
	className,
	icon,
	closeButton = false,
	fullWidth = false,
	dismissLabel = "Dismiss",
	onDismiss,
	children,
}: PropsWithChildren<AlertProps>) {
	const ref = useRef<HTMLDivElement>(null);

	const dismissAlert = () => {
		if (!ref.current) return;
		ref.current.closest(".grid-column-item-alertUmbBlock")?.remove();
	};

	const {
		container,
		content,
		icon: iconClassName,
		body,
		dismissButton,
		visuallyHidden,
	} = alertLayout();

	return (
		<div
			id={id}
			ref={ref}
			role="alert"
			className={cn(
				alert({ intent }),
				fullWidth && "alert-full-width",
				className,
			)}
		>
			<div className={cn(fullWidth ? container() : "")}>
				<div className={content()}>
					{icon ? <span className={iconClassName()}>{icon}</span> : null}
					<div className={body()}>{children}</div>
					{closeButton ? (
						<Button
							slot="trigger"
							type="button"
							className={dismissButton()}
							style={{ color: "inherit" }}
							aria-label={dismissLabel}
							onPress={() => {
								dismissAlert();
								onDismiss?.();
							}}
						>
							<DismissIcon />
							<span className={visuallyHidden()}>{dismissLabel}</span>
						</Button>
					) : null}
				</div>
			</div>
		</div>
	);
}
